import { Button } from "@/components/ui/button";
import { ArrowDown, Code, Zap, Terminal, Cpu, Database, Globe, Rocket, Settings, Braces, Hash, AtSign, CircuitBoard, Wifi } from "lucide-react";
import heroTechBg from "@/assets/hero-tech-bg.jpg";
import CodeTypingAnimation from "./CodeTypingAnimation";
import { useTranslation } from 'react-i18next';

const HeroSection = () => {
  const { t } = useTranslation();
  
  const scrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 cyber-grid opacity-30"></div>
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${heroTechBg})` }}
      ></div>
      
      {/* Code Typing Animation */}
      <CodeTypingAnimation />
      
      {/* Floating Elements */}
      <div className="absolute top-[15%] left-[8%] animate-float">
        <Code className="w-8 h-8 text-cyber-blue opacity-60" />
      </div>
      <div className="absolute top-[25%] right-[12%] animate-float" style={{ animationDelay: '1s' }}>
        <Zap className="w-6 h-6 text-cyber-purple opacity-60" />
      </div>
      <div className="absolute top-[45%] left-[15%] animate-float" style={{ animationDelay: '2s' }}>
        <Terminal className="w-7 h-7 text-cyber-blue opacity-50" />
      </div>
      <div className="absolute top-[20%] right-[25%] animate-float" style={{ animationDelay: '0.5s' }}>
        <Cpu className="w-5 h-5 text-cyber-purple opacity-70" />
      </div>
      <div className="absolute bottom-[35%] left-[10%] animate-float" style={{ animationDelay: '1.5s' }}>
        <Database className="w-6 h-6 text-cyber-blue opacity-40" />
      </div>
      <div className="absolute bottom-[25%] right-[20%] animate-float" style={{ animationDelay: '2.5s' }}>
        <Globe className="w-7 h-7 text-cyber-purple opacity-50" />
      </div>
      <div className="absolute top-[65%] left-[25%] animate-float" style={{ animationDelay: '3s' }}>
        <Rocket className="w-5 h-5 text-cyber-blue opacity-60" />
      </div>
      <div className="absolute bottom-[30%] right-[35%] animate-float" style={{ animationDelay: '0.8s' }}>
        <Settings className="w-6 h-6 text-cyber-purple opacity-45" />
      </div>
      <div className="absolute top-[70%] right-[8%] animate-float" style={{ animationDelay: '1.2s' }}>
        <Braces className="w-8 h-8 text-cyber-blue opacity-35" />
      </div>
      <div className="absolute top-[12%] left-[35%] animate-float" style={{ animationDelay: '2.8s' }}>
        <Hash className="w-4 h-4 text-cyber-purple opacity-55" />
      </div>
      <div className="absolute bottom-[15%] left-[5%] animate-float" style={{ animationDelay: '3.5s' }}>
        <AtSign className="w-6 h-6 text-cyber-blue opacity-50" />
      </div>
      <div className="absolute top-[55%] right-[30%] animate-float" style={{ animationDelay: '4s' }}>
        <CircuitBoard className="w-7 h-7 text-cyber-purple opacity-40" />
      </div>
      <div className="absolute bottom-[45%] left-[30%] animate-float" style={{ animationDelay: '0.3s' }}>
        <Wifi className="w-5 h-5 text-cyber-blue opacity-65" />
      </div>
      <div className="absolute top-[18%] right-[45%] animate-float" style={{ animationDelay: '1.8s' }}>
        <Terminal className="w-4 h-4 text-cyber-purple opacity-50" />
      </div>
      <div className="absolute bottom-[12%] right-[15%] animate-float" style={{ animationDelay: '2.2s' }}>
        <Code className="w-6 h-6 text-cyber-blue opacity-40" />
      </div>
      <div className="absolute top-[40%] left-[5%] animate-float" style={{ animationDelay: '3.2s' }}>
        <Zap className="w-5 h-5 text-cyber-purple opacity-55" />
      </div>
      <div className="absolute bottom-[40%] right-[10%] animate-float" style={{ animationDelay: '4.2s' }}>
        <Cpu className="w-7 h-7 text-cyber-blue opacity-45" />
      </div>
      <div className="absolute top-[35%] left-[40%] animate-float" style={{ animationDelay: '0.7s' }}>
        <Database className="w-4 h-4 text-cyber-purple opacity-60" />
      </div>
      <div className="absolute bottom-[35%] left-[45%] animate-float" style={{ animationDelay: '1.7s' }}>
        <Globe className="w-5 h-5 text-cyber-blue opacity-50" />
      </div>
      <div className="absolute top-[75%] right-[40%] animate-float" style={{ animationDelay: '2.7s' }}>
        <Rocket className="w-6 h-6 text-cyber-purple opacity-40" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center max-w-6xl mx-auto px-6">
        <div className="mb-8 animate-slide-in">
          <h1 className="text-5xl md:text-8xl font-bold mb-4 text-glow-cyber">
            <span className="bg-gradient-cyber bg-clip-text text-transparent">
              {t('hero.title')}
            </span>
            <br />
            <span className="text-foreground">{t('hero.subtitle')}</span>
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t('hero.description')}
          </p>
        </div>

        <div className="flex justify-center items-center animate-slide-in" style={{ animationDelay: '0.3s' }}>
          <Button 
            size="lg" 
            className="bg-gradient-cyber text-primary-foreground hover-glow border-0 px-8 py-4 text-lg font-semibold"
            onClick={scrollToServices}
          >
            {t('hero.exploreServices')}
          </Button>
        </div>

      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 animate-pulse-glow">
        <ArrowDown 
          className="w-8 h-8 text-cyber-blue cursor-pointer hover:text-cyber-purple transition-colors" 
          onClick={scrollToServices}
        />
      </div>
    </section>
  );
};

export default HeroSection;